import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PdfCanvasViewer } from "@/components/pdf-canvas-viewer";
import { attachmentDownloadUrl, downloadAttachmentFile, getToken } from "@/lib/api";

export interface AttachmentPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    /** Attachment UUID. Required when `open` is true. */
    attachmentId: string | null;
    /** Display name shown in the header. */
    fileName?: string | null;
    /** Optional blob URL to preview directly (e.g. form-template sample fill). */
    blobUrlOverride?: string | null;
    /** When using blobUrlOverride, optional download filename. */
    suggestedFilename?: string | null;
    /**
     * When false, the Download button is hidden and the file is view-only.
     * Used for sensitive artifacts like customer signatures that must be
     * viewable inline but never saved as a raw file. Defaults to true.
     */
    allowDownload?: boolean;
}

function isPdfName(name: string | null | undefined, mime: string): boolean {
    if (mime === "application/pdf") return true;
    return (name ?? "").toLowerCase().endsWith(".pdf");
}

function isImageMime(mime: string): boolean {
    return mime.startsWith("image/");
}

/**
 * In-app attachment viewer for PDFs and images. Fetches with auth (or uses a
 * provided blob URL), renders inline in a Dialog with Close + Download so
 * standalone PWA users are never trapped in a dead browser tab.
 *
 * PDFs render via pdf.js canvas (not iframe) because iOS WebKit cannot
 * display PDFs inside an iframe.
 */
export function AttachmentPreviewDialog({
    open,
    onClose,
    attachmentId,
    fileName,
    blobUrlOverride,
    suggestedFilename,
    allowDownload = true,
}: AttachmentPreviewDialogProps) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
    const [mimeType, setMimeType] = useState<string>("application/octet-stream");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadFilename, setDownloadFilename] = useState<string | null>(null);
    // Tracks whether an image blob has actually decoded/painted so we can keep
    // a spinner up until the <img> fires onLoad (otherwise the modal flashes a
    // blank panel between fetch-complete and paint).
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (!open) return;

        if (blobUrlOverride) {
            let cancelled = false;
            setLoading(true);
            setError(null);
            setImageLoaded(false);
            setBlobUrl(blobUrlOverride);
            setPdfData(null);
            setMimeType("application/pdf");
            setDownloadFilename(suggestedFilename ?? fileName ?? "preview.pdf");

            fetch(blobUrlOverride)
                .then(async (res) => {
                    if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
                    const buf = await res.arrayBuffer();
                    if (cancelled) return;
                    setPdfData(buf);
                })
                .catch((err) => {
                    if (!cancelled) setError(err instanceof Error ? err.message : "Preview failed");
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });

            return () => {
                cancelled = true;
            };
        }

        if (!attachmentId) return;

        let cancelled = false;
        let createdUrl: string | null = null;
        setLoading(true);
        setError(null);
        setImageLoaded(false);
        setBlobUrl(null);
        setPdfData(null);

        const token = getToken();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        fetch(attachmentDownloadUrl(attachmentId, { disposition: "inline" }), { headers })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
                const blob = await res.blob();
                let filename = fileName ?? `attachment-${attachmentId.slice(0, 8)}`;
                const disposition = res.headers.get("Content-Disposition");
                if (disposition) {
                    const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
                    const plain = disposition.match(/filename="?([^";]+)"?/i);
                    if (star) filename = decodeURIComponent(star[1]);
                    else if (plain) filename = plain[1];
                }
                if (cancelled) return;
                const mime = blob.type || res.headers.get("Content-Type") || "application/octet-stream";
                createdUrl = URL.createObjectURL(blob);
                setBlobUrl(createdUrl);
                setMimeType(mime);
                setDownloadFilename(filename);
                if (isPdfName(filename, mime)) {
                    setPdfData(await blob.arrayBuffer());
                }
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : "Preview failed");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [open, attachmentId, fileName, blobUrlOverride, suggestedFilename]);

    useEffect(() => {
        if (!open) {
            if (blobUrl && !blobUrlOverride) {
                URL.revokeObjectURL(blobUrl);
            }
            setBlobUrl(null);
            setPdfData(null);
        }
    }, [open, blobUrl, blobUrlOverride]);

    const title = fileName?.trim() ? `Preview · ${fileName}` : "Preview";
    const showPdf = isPdfName(downloadFilename ?? fileName, mimeType);
    const showImage = blobUrl && !showPdf && isImageMime(mimeType);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={title}
            className="!max-w-5xl w-[min(96vw,1100px)] !p-0 max-h-[92vh]"
        >
            <div className="flex h-[78vh] flex-col">
                <div className="relative flex flex-1 flex-col overflow-hidden bg-muted/40">
                    {(loading || (showImage && !imageLoaded)) && !error && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/40 text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-destructive">
                            <X className="h-5 w-5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {showPdf && !error && pdfData && (
                        <PdfCanvasViewer data={pdfData} className="relative flex h-full w-full flex-col" />
                    )}
                    {showImage && !error && (
                        <div className="flex flex-1 items-center justify-center overflow-hidden">
                            <img
                                src={blobUrl}
                                alt={fileName ?? "Attachment"}
                                className="max-h-full max-w-full object-contain"
                                onLoad={() => setImageLoaded(true)}
                                onError={() => setError("Preview failed")}
                            />
                        </div>
                    )}
                    {blobUrl && !error && !showPdf && !showImage && !loading && (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                            <span>Preview not available for this file type.</span>
                            <span className="text-xs">Use Download to save the file.</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 border-t bg-card px-5 py-3">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                    {allowDownload && attachmentId && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (!attachmentId) return;
                                downloadAttachmentFile(attachmentId, downloadFilename ?? fileName ?? undefined).catch(
                                    () => setError("Download failed"),
                                );
                            }}
                        >
                            <Download className="mr-1.5 h-4 w-4" /> Download
                        </Button>
                    )}
                    {allowDownload && !attachmentId && blobUrl && downloadFilename && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                const a = document.createElement("a");
                                a.href = blobUrl;
                                a.download = downloadFilename;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                            }}
                        >
                            <Download className="mr-1.5 h-4 w-4" /> Download
                        </Button>
                    )}
                </div>
            </div>
        </Dialog>
    );
}
